import { useQuery, useReactiveVar } from '@apollo/client'
import { Close, Groups, Person } from '@mui/icons-material'
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined'
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography, styled } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import theme from '../../../../config/theme'
import { BucketType, BucketUserDocument, BucketUserRole } from '../../../../graphql/generated'
import { modalVar } from '../../../../providers/apollo/cache'
import useBucket from '../../hooks/useBucket'
import useBucketPlan from '../../hooks/useBucketPlan'
import useBucketUpdate from '../../hooks/useBucketUpdate'

interface Fields {
  bucketName: string
}

const TitleText = styled(DialogTitle)({
  fontFamily: 'Outfit',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '13px',
  pb: 1,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
})

const StyledTextButton = styled(Button)({
  padding: '2px 0',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main
  }
})

export default function BucketSettings() {
  const [fields, setFields] = useState<Fields>({ bucketName: '' })
  const isBucketDeleteEnabled = useFlag('isBucketDeleteEnabled')
  const modal = useReactiveVar(modalVar)
  const { mutation } = useBucketUpdate()
  const { bucket, bucketUser, currentUserIsOwner, isDefaultTitle } = useBucket()
  const { isSharingEnabled } = useBucketPlan()
  const navigate = useNavigate()
  const isGroupBucket = (bucket?.bucketUserCount ?? 0) > 1
  const bucketId = bucket?.id
  const ownerId = bucket?.ownerId
  const ownerQuery = useQuery(BucketUserDocument, {
    skip: !ownerId || !bucketId,
    variables: { userId: ownerId!, bucketId: bucketId! }
  })
  const isSharingPlan = bucket?.plan?.is_sharing_enabled

  const ownerString =
    bucketUser?.id === ownerQuery.data?.bucketUser?.id ? 'You' : ownerQuery.data?.bucketUser?.userProfile?.email ?? ownerQuery.data?.bucketUser?.id

  const shraredWithString = bucket?.bucketUserCount === 1 ? 'You are the only member' : `Shared with ${bucket?.bucketUserCount} people`

  const canUpdateBucket = bucketUser?.permissions?.bucketUpdate

  useEffect(() => {
    if (bucket?.title) {
      setFields({ bucketName: bucket.title })
    }
  }, [bucket])

  const onClose = () => {
    modalVar(null)
    setFields({ bucketName: '' })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFields({ ...fields, [event.target.name]: event.target.value })
  }

  const handleInvite = () => {
    if (!isGroupBucket && isSharingEnabled && !isDefaultTitle) {
      mutation({
        variables: {
          bucketId: bucket?.id || '',
          data: { bucketType: BucketType.Group }
        },
        onCompleted: () => {
          modalVar('bucketInvite')
        }
      })
    } else {
      const modalType = isSharingPlan && !(bucket.bucketType === BucketType.Mybucket) ? 'bucketInvite' : 'bucketUpgrade'
      modalVar(modalType)
    }
  }

  const onSave = () => {
    if (!fields.bucketName || !bucket?.id) {
      return
    }

    const variables = { bucketId: bucket.id, data: { title: fields.bucketName } }
    mutation({
      variables,
      onCompleted: () => {
        onClose()
      }
    })
  }

  const onPressManagePermissions = () => {
    onClose()
    navigate(`/bucket/${bucketId}/sharing`)
  }

  const onPressManageMembership = () => {
    onClose()
    navigate(`/buckets`)
  }

  return (
    <Dialog
      open={modal === 'bucketSettings'}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: 3,
          p: 1
        }
      }}
    >
      <TitleText
        sx={{
          fontSize: 20,
          fontWeight: 'bold',
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        Bucket Settings
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </TitleText>

      <DialogContent sx={{ pt: 2 }}>
        <Stack sx={{ mb: '16px', mt: 1 }}>
          {canUpdateBucket ? (
            <>
              <TextField
                fullWidth
                label="Bucket Name"
                variant="outlined"
                size="small"
                name="bucketName"
                value={fields.bucketName || ''}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && fields.bucketName && fields.bucketName !== bucket?.title) {
                    onSave()
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              />
              <Button
                variant="text"
                onClick={onSave}
                disabled={!fields.bucketName || fields.bucketName === bucket?.title || fields.bucketName === ''}
                sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-end', padding: '4px' }}
              >
                Save Bucket Name
              </Button>
            </>
          ) : (
            <TitleText color="text.primary" sx={{ fontSize: '1.125rem', fontWeight: 500, padding: '4px 0' }}>
              {bucket?.title}
            </TitleText>
          )}
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {/* add image icon */}
          <PersonPinOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: 22 }} />
          <Typography variant="body2" color="text.secondary">
            Created by {ownerString}
          </Typography>
        </Box>

        <Stack direction="row">
          {isGroupBucket && (
            <>
              <Groups fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: 22 }} />
              <Stack>
                <Typography variant="body2" color="text.secondary">
                  {shraredWithString}
                </Typography>
                <Box>
                  <StyledTextButton variant="text" onClick={onPressManagePermissions}>
                    Manage Permissions
                  </StyledTextButton>
                </Box>
              </Stack>
            </>
          )}
          {!isGroupBucket && (
            <>
              <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: 22 }} />
              <Stack>
                <Typography variant="body2" color="text.secondary">
                  Personal
                </Typography>
                <Box>
                  <StyledTextButton variant="text" onClick={handleInvite}>
                    Invite People
                  </StyledTextButton>
                </Box>
              </Stack>
            </>
          )}
        </Stack>

        <Stack justifyContent="flex-start" sx={{ gap: '4px', marginTop: '16px' }}>
          <Box>
            {bucketUser?.userRole !== BucketUserRole.Owner && (
              <StyledTextButton variant="text" onClick={onPressManageMembership}>
                Manage Bucket Membership
              </StyledTextButton>
            )}
          </Box>
          {isBucketDeleteEnabled && currentUserIsOwner && (
            <div>
              <StyledTextButton
                variant="text"
                sx={{ color: theme.palette.customError[500] }}
                data-testid="bucket-settings-delete-bucket"
                onClick={() => modalVar('bucketDelete')}
              >
                Delete Bucket
              </StyledTextButton>
            </div>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
